import React from 'react';
import { getEmbedOrigin } from '../lib/origin';

// Drop-in replacement for next/image, aliased at bundle time. next/image's
// optimisation pipeline is server-side only; visually a plain <img> with the
// same src / sizing / className / style is identical. The one thing we must
// fix is root-relative asset paths (/DarkFamewallLogo.svg, /appstore.svg, ...)
// which would otherwise resolve against the customer's domain - they get
// rebased onto the embed origin.

function resolveSrc(src) {
  if (!src) {
    return src;
  }

  let value = src;

  if (typeof value === 'object') {
    value = value.src || value.default || '';
  }

  if (typeof value !== 'string' || value === '') {
    return value;
  }

  if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:')) {
    return value;
  }

  if (value.startsWith('/')) {
    return getEmbedOrigin() + value;
  }

  return value;
}

function toDimension(value) {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  if (typeof value === 'number') {
    return value;
  }

  const parsed = parseInt(value, 10);

  return Number.isNaN(parsed) ? undefined : parsed;
}

const NextImage = React.forwardRef(function NextImage(props, ref) {
  const {
    src,
    alt = '',
    width,
    height,
    className,
    style,
    loading,
    priority,
    layout,
    objectFit,
    objectPosition,
    placeholder,
    blurDataURL,
    quality,
    sizes,
    unoptimized,
    onLoadingComplete,
    onLoad,
    fill,
    ...rest
  } = props;

  const imgStyle = { ...(style || {}) };
  let imgWidth = toDimension(width);
  let imgHeight = toDimension(height);

  if (layout === 'fill' || fill) {
    if (imgStyle.position === undefined) imgStyle.position = 'absolute';
    if (imgStyle.top === undefined) imgStyle.top = 0;
    if (imgStyle.left === undefined) imgStyle.left = 0;
    if (imgStyle.right === undefined) imgStyle.right = 0;
    if (imgStyle.bottom === undefined) imgStyle.bottom = 0;
    if (imgStyle.width === undefined) imgStyle.width = '100%';
    if (imgStyle.height === undefined) imgStyle.height = '100%';
    imgWidth = undefined;
    imgHeight = undefined;
  } else if (layout === 'responsive') {
    if (imgStyle.width === undefined) imgStyle.width = '100%';
    if (imgStyle.height === undefined) imgStyle.height = 'auto';
  }

  if (objectFit && imgStyle.objectFit === undefined) {
    imgStyle.objectFit = objectFit;
  }

  if (objectPosition && imgStyle.objectPosition === undefined) {
    imgStyle.objectPosition = objectPosition;
  }

  const handleLoad = (event) => {
    if (typeof onLoadingComplete === 'function') {
      try {
        onLoadingComplete(event.currentTarget);
      } catch (err) {
        // ignore - matches next/image not surfacing callback errors
      }
    }

    if (typeof onLoad === 'function') {
      onLoad(event);
    }
  };

  return React.createElement('img', {
    ref,
    src: resolveSrc(src),
    alt: alt || '',
    width: imgWidth,
    height: imgHeight,
    className,
    style: imgStyle,
    loading: priority ? 'eager' : loading || 'lazy',
    decoding: 'async',
    onLoad: handleLoad,
    ...rest,
  });
});

export default NextImage;
